import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdAdd, MdSearch, MdCallMade, MdCallReceived, MdMoreVert, MdVisibility, MdEdit, MdDelete } from 'react-icons/md';
import axios from 'axios';
import { GET_RECEIPT_LIST_API, DELETE_RECEIPT_API } from '../config/API';
import ActionMenuPortal from '../components/common/ActionMenuPortal';
import Loader from '../components/common/Loader';
import NoData from '../components/common/NoData';
import Pagination from '../components/common/Pagination';
import { useUser } from '../context/UserContext';
import Swal from 'sweetalert2';
import { showErrorToast, showSuccessToast } from '../utils/toastUtils';
import './PaymentManagement.css';

const PaymentManagement = () => {
    const navigate = useNavigate();
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [openMenuId, setOpenMenuId] = useState(null);
    const [anchorRect, setAnchorRect] = useState(null);

    const { hasPermission } = useUser();

    const toggleMenu = (id, e) => {
        e.stopPropagation();
        if (openMenuId === id) {
            setOpenMenuId(null);
            setAnchorRect(null);
        } else {
            setOpenMenuId(id);
            setAnchorRect(e.currentTarget.getBoundingClientRect());
        }
    };


    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Close menu when clicking outside or pressing Escape
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (event.target.closest('.action-portal-wrapper')) return;
            if (event.target.closest('.action-menu-btn')) return;
            setOpenMenuId(null);
            setAnchorRect(null);
        };

        const handleEscKey = (event) => {
            if (event.key === 'Escape') {
                setOpenMenuId(null);
                setAnchorRect(null);
            }
        };

        document.addEventListener('click', handleClickOutside, true);
        document.addEventListener('keydown', handleEscKey, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
            document.removeEventListener('keydown', handleEscKey, true);
        };
    }, []);

    // Reset to page 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch]);

    useEffect(() => {
        fetchPayments();
    }, [currentPage, debouncedSearch]);

    const fetchPayments = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(`${GET_RECEIPT_LIST_API}?page=${currentPage}&limit=10${debouncedSearch ? `&search=${debouncedSearch}` : ''}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status) {
                setPayments(response.data.data || []);
                const pagination = response.data.pagination || {};
                setTotalPages(pagination.totalPages || response.data.totalPages || 1);
                setTotalRecords(pagination.total || pagination.totalRecords || response.data.totalRecords || 0);
            }
        } catch (error) {
            console.error('Error fetching payments:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Delete Payment?',
            text: "This action cannot be undone and will restore the PO balances.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#4b5563',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-confirm-btn delete-confirm-btn',
                cancelButton: 'swal-cancel-btn',
            },
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const token = localStorage.getItem('accessToken');
                const response = await axios.delete(DELETE_RECEIPT_API(id), {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    showSuccessToast('Payment deleted successfully');
                    // If we're on the last item of the last page, go to previous page
                    if (payments.length === 1 && currentPage > 1) {
                        setCurrentPage(prev => prev - 1);
                    } else {
                        fetchPayments();
                    }
                } else {
                    showErrorToast(response.data.message || 'Failed to delete payment');
                }
            } catch (error) {
                console.error('Error deleting payment:', error);
                showErrorToast('An error occurred during deletion');
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <div className="purchase-orders-container">
            <div className="purchase-orders-header">
                <div className="header-left">
                    <h2>Payment Management</h2>
                </div>
                {hasPermission('PAYMENT_MANAGEMENT', 'create') && (
                    <button
                        className="add-po-btn"
                        onClick={() => navigate('/payment-management/new')}
                    >
                        <MdAdd /> Record New
                    </button>
                )}
            </div>

            <div className="card-container-po">
                {/* Search Bar */}
                <div className="search-container">
                    <div className="search-wrapper">
                        <MdSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search by partner, site or notes..."
                            className="search-input"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button
                                className="clear-search-btn"
                                onClick={() => setSearchTerm('')}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                </div>

                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Receipt No</th>
                                <th>Vendor</th>
                                <th>Project</th>
                                <th>Amount</th>
                                <th className="actions-header">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}><Loader /></td></tr>
                            ) : payments.length === 0 ? (
                                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}><NoData /></td></tr>
                            ) : (
                                payments.map((p) => (
                                    <tr key={p.id}>
                                        <td>{p.date?.split('T')[0]}</td>
                                        <td><span className="inward-no" style={{ fontFamily: 'monospace' }}>{p.receiptNo}</span></td>
                                        <td>
                                            <div className="vendor-info-name">{p.Vendor?.vendorName || 'N/A'}</div>
                                            <div className="vendor-info-company">{p.Vendor?.companyName || ''}</div>
                                        </td>
                                        <td>{p.ProjectMaster?.projectName || 'N/A'}</td>

                                        <td className="amount-cell">
                                            ₹{parseFloat(p.totalAmount || 0).toLocaleString()}
                                        </td>
                                        <td className="action-cell">
                                            <button
                                                className="action-menu-btn"
                                                onClick={(e) => toggleMenu(p.id, e)}
                                                title="Actions"
                                            >
                                                <MdMoreVert size={20} />
                                            </button>

                                            <ActionMenuPortal
                                                isOpen={openMenuId === p.id}
                                                anchorRect={anchorRect}
                                                onClose={() => setOpenMenuId(null)}
                                            >
                                                <div className="action-dropdown shadow-lg">
                                                    {hasPermission('PAYMENT_MANAGEMENT', 'read') && (
                                                        <button className="action-item" onClick={() => navigate(`/payment-management/view/${p.id}`)}>
                                                            <MdVisibility size={16} /> View Details
                                                        </button>
                                                    )}
                                                    {hasPermission('PAYMENT_MANAGEMENT', 'update') && (
                                                        <button className="action-item" onClick={() => navigate(`/payment-management/edit/${p.id}`)}>
                                                            <MdEdit size={16} /> Edit Payment
                                                        </button>
                                                    )}
                                                    {hasPermission('PAYMENT_MANAGEMENT', 'delete') && (
                                                        <button className="action-item delete-item" onClick={() => handleDelete(p.id || p._id)}>
                                                            <MdDelete size={16} /> Delete
                                                        </button>
                                                    )}
                                                </div>
                                            </ActionMenuPortal>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Mobile Card View */}
                <div className="mobile-card-view">
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '2rem' }}><Loader /></div>
                    ) : payments.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '2rem' }}><NoData /></div>
                    ) : (
                        payments.map((p) => (
                            <div key={p.id} className="dashboard-card">
                                <div className="card-header">
                                    <div className="card-title">
                                        <strong>{p.date?.split('T')[0]}</strong>
                                        <span className="status-badge status-closed status-badge-receipt">
                                            <MdCallReceived size={14} />
                                            {p.receiptNo}
                                        </span>
                                    </div>
                                    <button
                                        className="action-menu-btn"
                                        onClick={(e) => toggleMenu(p.id, e)}
                                        title="Actions"
                                    >
                                        <MdMoreVert size={20} />
                                    </button>
                                    <ActionMenuPortal
                                        isOpen={openMenuId === p.id}
                                        anchorRect={anchorRect}
                                        onClose={() => setOpenMenuId(null)}
                                    >
                                        <div className="action-dropdown shadow-lg">
                                            {hasPermission('PAYMENT_MANAGEMENT', 'read') && (
                                                <button className="action-item" onClick={() => navigate(`/payment-management/view/${p.id}`)}>
                                                    <MdVisibility size={16} /> View Details
                                                </button>
                                            )}
                                            {hasPermission('PAYMENT_MANAGEMENT', 'update') && (
                                                <button className="action-item" onClick={() => navigate(`/payment-management/edit/${p.id}`)}>
                                                    <MdEdit size={16} /> Edit Payment
                                                </button>
                                            )}
                                            {hasPermission('PAYMENT_MANAGEMENT', 'delete') && (
                                                <button className="action-item delete-item" onClick={() => handleDelete(p.id || p._id)}>
                                                    <MdDelete size={16} /> Delete
                                                </button>
                                            )}
                                        </div>
                                    </ActionMenuPortal>
                                </div>
                                <div className="card-body">
                                    <div className="card-row">
                                        <span className="card-label">Vendor</span>
                                        <span className="card-value"><strong>{p.Vendor?.vendorName || 'N/A'}</strong></span>
                                    </div>
                                    <div className="card-row">
                                        <span className="card-label">Project</span>
                                        <span className="card-value">{p.ProjectMaster?.projectName || 'N/A'}</span>
                                    </div>
                                    <div className="card-row">
                                        <span className="card-label">Amount</span>
                                        <span className="card-value amount-cell">
                                            ₹{parseFloat(p.totalAmount || 0).toLocaleString()}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    totalRecords={totalRecords}
                    currentCount={payments.length}
                    entityName="Payments"
                />
            </div>
        </div>
    );
};

export default PaymentManagement;
